import {
	Badge,
	Button,
	Can,
	DataTable,
	type DataTableFiltersProp,
	toast,
	useDialogController,
} from "@k3-tech/react-kit";
import type { ColumnDef } from "@tanstack/react-table";
import { EditIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { SalesChannelCategoryRow } from "../usecases/useSalesChannelCategoryList";
import { useSalesChannelCategoryMutations } from "../usecases/useSalesChannelCategoryMutations";
import { useSalesChannelCategoryPagedList } from "../usecases/useSalesChannelCategoryPagedList";
import {
	type CategoryFormValues,
	SalesChannelCategoryFormDialog,
} from "./SalesChannelCategoryFormDialog";

export interface SalesChannelCategoryTableProps {
	groupID: string;
	onChanged?: () => void;
}

export function SalesChannelCategoryTable({
	groupID,
	onChanged,
}: SalesChannelCategoryTableProps) {
	const { confirm } = useDialogController();
	const categories = useSalesChannelCategoryPagedList(groupID);
	const mutations = useSalesChannelCategoryMutations();

	const [dialog, setDialog] = useState<
		{ mode: "create" } | { mode: "edit"; row: SalesChannelCategoryRow } | null
	>(null);

	const submit = async (values: CategoryFormValues) => {
		const payload = {
			salesChannelCategoryGroupID: groupID,
			salesChannelCategoryName: values.salesChannelCategoryName,
			displayOrder: Number(values.displayOrder) || 1,
			isActive: values.isActive,
			isInMenu: values.isInMenu,
		};
		const result =
			dialog?.mode === "edit"
				? await mutations.update({
						...payload,
						salesChannelCategoryID: dialog.row.salesChannelCategoryID,
					})
				: await mutations.create(payload);
		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}
		toast.success(
			dialog?.mode === "edit" ? "Category updated" : "Category added",
		);
		setDialog(null);
		categories.load();
		onChanged?.();
	};

	const remove = async (row: SalesChannelCategoryRow) => {
		const ok = await confirm({
			title: `Delete ${row.salesChannelCategoryName}?`,
			description: "This action cannot be undone.",
			destructive: true,
			confirmText: "Delete",
			cancelText: "Cancel",
		});
		if (!ok) return;
		const result = await mutations.remove(row.salesChannelCategoryID);
		if (result.error) {
			toast.error(result.error.message);
			return;
		}
		toast.success("Category removed");
		categories.load();
		onChanged?.();
	};

	const columns: ColumnDef<SalesChannelCategoryRow>[] = [
		{ header: "Order", accessorKey: "displayOrder" },
		{ header: "Category", accessorKey: "salesChannelCategoryName" },
		{
			header: "Status",
			id: "status",
			cell: ({ row }) =>
				row.original.isInMenu ? (
					<Badge variant="secondary" className="text-green-600">
						In menu
					</Badge>
				) : (
					<Badge variant="secondary">Hidden</Badge>
				),
		},
		{
			header: "",
			id: "actions",
			cell: ({ row }) => (
				<div className="flex items-center justify-end gap-1">
					<Can permissions="sales_channel_category_groups.edit">
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0"
							title="Edit"
							onClick={() => setDialog({ mode: "edit", row: row.original })}
						>
							<EditIcon className="h-3.5 w-3.5" />
						</Button>
					</Can>
					<Can permissions="sales_channel_category_groups.delete">
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0"
							title="Delete"
							onClick={() => remove(row.original)}
						>
							<TrashIcon className="h-3.5 w-3.5" />
						</Button>
					</Can>
				</div>
			),
		},
	];

	const formFilters = useMemo<DataTableFiltersProp>(
		() => [
			{
				variant: "plain",
				layout: "flex",
				flex: { direction: "row", wrap: true, gap: "gap-3" },
				fields: [
					{
						name: "salesChannelCategoryName",
						label: " ",
						type: "text",
						placeholder: "Search category",
					},
					{
						name: "status",
						label: " ",
						type: "select",
						defaultValue: null,
						options: [
							{ label: "All status", value: null },
							{ label: "Active", value: true },
							{ label: "Inactive", value: false },
						],
					},
				],
			},
		],
		[],
	);

	const onFormFilterChange = (values: Record<string, unknown>) => {
		categories.setFilters({
			salesChannelCategoryName: values.salesChannelCategoryName || null,
			isActive: values.status as boolean | undefined,
		});
	};

	return (
		<>
			<div className="mb-3 flex justify-end">
				<Can permissions="sales_channel_category_groups.create">
					<Button
						size="sm"
						variant="outline"
						onClick={() => setDialog({ mode: "create" })}
					>
						<PlusIcon className="h-3.5 w-3.5" />
						Add
					</Button>
				</Can>
			</div>
			<DataTable<SalesChannelCategoryRow, unknown>
				columns={columns}
				data={categories.items as SalesChannelCategoryRow[]}
				filterWrapper="none"
				filterShowActionsSeparator={false}
				loading={categories.fetching}
				formFilters={formFilters}
				onFormFilterChange={onFormFilterChange}
				pagination={categories.rowCount > 0}
				paginationState={categories.paginationState}
				onPaginationChange={categories.setPagination}
				rowCount={categories.rowCount}
				showStandardActions
				onRefresh={categories.load}
			/>

			<SalesChannelCategoryFormDialog
				open={!!dialog}
				onOpenChange={(open) => !open && setDialog(null)}
				mode={dialog?.mode ?? "create"}
				defaultValues={
					dialog?.mode === "edit"
						? {
								salesChannelCategoryName: dialog.row.salesChannelCategoryName,
								displayOrder: dialog.row.displayOrder,
								isActive: dialog.row.isActive,
								isInMenu: dialog.row.isInMenu,
							}
						: undefined
				}
				onSubmit={submit}
			/>
		</>
	);
}
